import { useFormContext } from 'react-hook-form'
import {
  TrashIcon,
  CurecallConfirmationModal,
  PlusIcon,
  CurecallButton,
  CurecallTextButton,
  ECurecallButtonAppearance,
  ECurecallSize,
  SparklesIcon,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useState, useMemo } from 'react'
import { TNodeFormValues, TNodeVariable } from '../node.type'
import { TVariable, EVariableType } from 'src/types/variable'
import { type TPatientLocale } from 'src/types/locales'
import { SelectOrAddVariableModal } from './variable/select-or-add-variable-modal.component'
import { useProjectVariables } from './use-project-variables.hook'
import { useChatbotFlow } from '../chatbot.provider'
import { NodeConditionalSections } from './conditional-actions/node-conditional-sections.component'
import { NodeClarificationMessageField } from './node-clarification-message-field.component'

export function NodeVariableFields() {
  const { t } = useTranslation()
  const { watch, setValue } = useFormContext<TNodeFormValues>()
  const { data: variables = [] } = useProjectVariables()
  const { updateCurrentNodeData } = useChatbotFlow()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const variable = watch('variable')
  const messagesByLanguage = watch('messagesByLanguage')

  const selectedVariable = useMemo(
    () => variables.find((v: TVariable) => v.id === variable?.id),
    [variables, variable?.id],
  )

  const updateVariable = (newVariable: TVariable | undefined) => {
    if (newVariable === undefined) {
      setValue('variable', undefined)
      updateCurrentNodeData({ variable: undefined })
      return
    }

    // STRING (free-text): captured via VariableId, no branching, no clarification loop.
    if (newVariable.type === EVariableType.STRING) {
      const stringVariable: TNodeVariable = { id: newVariable.id, type: EVariableType.STRING }
      setValue('variable', stringVariable)
      setValue('clarificationMessage', undefined)
      updateCurrentNodeData({ variable: stringVariable, clarificationMessage: undefined })
      return
    }

    const nodeVariable: TNodeVariable = (() => {
      if (newVariable.type === EVariableType.BOOLEAN) {
        return { id: newVariable.id, type: EVariableType.BOOLEAN }
      }
      if (newVariable.type === EVariableType.MEDIA) {
        return { id: newVariable.id, type: EVariableType.MEDIA, isRequired: true }
      }
      return {
        id: newVariable.id,
        type: EVariableType.INTEGER_VALUE_RANGE,
        minValue: newVariable.minValue ?? 0,
        maxValue: newVariable.maxValue ?? 0,
        condition: {
          ranges: [
            {
              lowerBound: newVariable.minValue ?? 0,
              upperBound: Number.NaN,
              action: {},
            },
          ],
        },
      }
    })()

    // Generate default clarification message for each language in messagesByLanguage
    const clarificationMessage: Partial<Record<TPatientLocale, string>> = {}
    if (messagesByLanguage) {
      Object.keys(messagesByLanguage).forEach((lang) => {
        const locale = lang as TPatientLocale
        clarificationMessage[locale] = getDefaultClarificationMessage(
          locale,
          newVariable.type,
          newVariable.minValue,
          newVariable.maxValue,
        )
      })
    }

    setValue('variable', nodeVariable)
    setValue('clarificationMessage', clarificationMessage)
    updateCurrentNodeData({ variable: nodeVariable, clarificationMessage })
  }

  const getDefaultClarificationMessage = (
    locale: TPatientLocale,
    type: EVariableType,
    minValue = 0,
    maxValue = 0,
  ): string => {
    const messages: Record<TPatientLocale, Partial<Record<EVariableType, string>>> = {
      fr: {
        [EVariableType.BOOLEAN]: 'Merci de répondre par Oui ou Non à la question posée.',
        [EVariableType.INTEGER_VALUE_RANGE]: `Merci de répondre par un chiffre compris entre ${minValue} et ${maxValue}.`,
        [EVariableType.MEDIA]: 'Merci de nous envoyer une photo ou un document PDF en réponse à ce message.',
      },
      en: {
        [EVariableType.BOOLEAN]: 'Please answer Yes or No to the question asked.',
        [EVariableType.INTEGER_VALUE_RANGE]: `Please answer with a number between ${minValue} and ${maxValue}.`,
        [EVariableType.MEDIA]: 'Please send a photo or a PDF document as a reply to this message.',
      },
      pl: {
        [EVariableType.BOOLEAN]: 'Proszę odpowiedzieć Tak lub Nie na zadane pytanie.',
        [EVariableType.INTEGER_VALUE_RANGE]: `Proszę odpowiedzieć liczbą od ${minValue} do ${maxValue}.`,
        [EVariableType.MEDIA]: 'Proszę przesłać zdjęcie lub dokument PDF w odpowiedzi na tę wiadomość.',
      },
      es: {
        [EVariableType.BOOLEAN]: 'Por favor, responda Sí o No a la pregunta planteada.',
        [EVariableType.INTEGER_VALUE_RANGE]: `Por favor, responda con un número entre ${minValue} y ${maxValue}.`,
        [EVariableType.MEDIA]: 'Por favor, envíe una foto o un documento PDF como respuesta a este mensaje.',
      },
    }

    return messages[locale]?.[type] || ''
  }

  const VARIABLE_TYPE_LABELS: Partial<Record<EVariableType, string>> = {
    [EVariableType.BOOLEAN]: t('pages.configuration.followups.chatbot.variable-modal.variable-type-boolean'),
    [EVariableType.INTEGER_VALUE_RANGE]: t(
      'pages.configuration.followups.chatbot.variable-modal.variable-type-integer-value-range',
      { min: selectedVariable?.minValue ?? 0, max: selectedVariable?.maxValue ?? 0 },
    ),
    [EVariableType.MEDIA]: t(
      'pages.configuration.followups.chatbot.variable-modal.variable-type-media-label',
    ),
    [EVariableType.STRING]: t('pages.configuration.followups.chatbot.variable-modal.variable-type-string'),
  }

  const handleRemoveVariable = () => {
    setShowDeleteConfirmation(true)
  }

  const handleConfirmDeleteVariable = () => {
    updateVariable(undefined)
    setValue('humanReviewCheck', undefined)
    updateCurrentNodeData({ humanReviewCheck: undefined })
    setShowDeleteConfirmation(false)
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
  }

  const handleModalValidate = (variable: TVariable) => {
    updateVariable(variable)
    setIsModalOpen(false)
  }

  if (!selectedVariable) {
    return (
      <>
        <CurecallTextButton
          type="button"
          iconLeft={<PlusIcon className="c-w-4 c-mr-2" />}
          iconRight={<SparklesIcon className="c-w-4 c-h-4 c-ml-1" />}
          onClick={() => setIsModalOpen(true)}
          className="c-w-max c-text-sm"
        >
          {t('pages.configuration.followups.chatbot.node-form.variable-association')}
        </CurecallTextButton>

        {isModalOpen && (
          <SelectOrAddVariableModal
            isOpen={isModalOpen}
            onCancel={handleModalCancel}
            onValidate={handleModalValidate}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div className="c-border c-border-gray-500 c-rounded-lg c-bg-gray-50">
        <div className="c-flex c-items-center c-justify-between c-animate-fade-in c-m-4">
          <div className="c-flex c-gap-2">
            <span className="c-text-sm c-font-bold c-text-gray-700">
              {VARIABLE_TYPE_LABELS[selectedVariable.type] ?? selectedVariable.type} :
            </span>
            <span className="c-text-sm c-text-gray-500">
              {selectedVariable.intlLabel?.fr || selectedVariable.intlLabel?.en}
            </span>
          </div>
          <CurecallButton
            iconType={<TrashIcon />}
            onClick={handleRemoveVariable}
            appearance={ECurecallButtonAppearance.CANCEL}
            size={ECurecallSize.SMALL}
            ariaLabelText={t('common.actions.delete')}
          />
        </div>

        {variable?.type !== EVariableType.STRING && (
          <>
            {!(variable?.type === EVariableType.MEDIA && variable.isRequired === false) && (
              <NodeClarificationMessageField />
            )}
            <NodeConditionalSections />
          </>
        )}
      </div>

      {showDeleteConfirmation && (
        <CurecallConfirmationModal
          color="ALERT"
          title={t('pages.configuration.followups.chatbot.node-form.delete-variable-title')}
          isOpen={true}
          cancelButtonLabel={t('common.actions.cancel')}
          confirmButtonLabel={t('common.actions.confirm')}
          onCancel={() => setShowDeleteConfirmation(false)}
          onConfirm={handleConfirmDeleteVariable}
        >
          {t('pages.configuration.followups.chatbot.node-form.delete-variable-body', {
            variableLabel: selectedVariable?.intlLabel?.fr || selectedVariable?.intlLabel?.en,
          })}
        </CurecallConfirmationModal>
      )}
    </>
  )
}
